import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Obj } from 'reselect/es/types';
import { RootState } from './store';
import { AVAILABLE, COMPLETE, Doc, FAILED, KeyValuePair, LanguageData, LanguagesData, LinkInfo, READY, TranslationData, UrlParams } from './types';
import locationCountries from './locationCountries'
import {rolesList, titlesList, usStatesList, caTerritoriesList, countryISOMapping} from './staticData'

export type ContactState = "undone" | "waiting" | "ready"

export type AppState = {
  designId?: string
  configId?: string
  productCode: string
  product?: string
  language?: string
  localeFullCode?: string
  country?: string
  countryName?:string
  release?: string
  productType?: string
  cdProductType?: string

  brochures: Doc[]
  drawings: Doc[]
  allDownloadable: boolean
  validTill: number
  orderHasBeenMade: boolean

  contactState: ContactState
  notifyState: ContactState

  currentEmail: string
  secondUserEmail?: string
  secondUserCount?: number

  terms: KeyValuePair
  languages: LanguageData[]
  domainCountry: KeyValuePair
  validUrlParams: boolean
  linkInfo?: LinkInfo

  requestReachedTheBackend: boolean

  showTranslationsKeys: boolean
  canShowTranslationsKeys: boolean

  frontlineCountries?:any[]

  showContactForm:boolean

  cdProducts?:any
  cdUri?:string

  initialized: boolean

  rowKey:string | null
}

const initialState: AppState = {
  brochures: [],
  drawings: [],  
  allDownloadable: false,
  validTill: 0,
  orderHasBeenMade:false,
  contactState: "undone",
  notifyState: "undone",
  currentEmail: "",
  terms: {},
  languages: [],
  domainCountry: [],
  productCode:'',
  validUrlParams:false,
  requestReachedTheBackend: false,
  showTranslationsKeys: false,
  canShowTranslationsKeys: false,
  showContactForm:false,
  initialized: false,
  rowKey: null,
};

const ONE_YEAR = 1000*60*60*24*365

export const countryList = locationCountries
export const roles = rolesList
export const titles = titlesList
export const usStates = usStatesList
export const caTerritories = caTerritoriesList

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload
    },

    setRowKey: (state, action: PayloadAction<string>) => {
      state.rowKey = action.payload
    },
    
    setRequestReachedTheBackend: (state, action: PayloadAction<boolean>) => {
      state.requestReachedTheBackend = action.payload
    },

    setShowContactForm: (state, action: PayloadAction<boolean>) => {
      state.showContactForm = action.payload
    },

    setProductType: (state, action: PayloadAction<string>) => {
      state.productType = action.payload
    },

    setValidUrlParams: (state, action: PayloadAction<boolean>) => {
      state.validUrlParams = action.payload
    },

    setOrderHasBeenMade: (state, action: PayloadAction<boolean>) => {
      state.orderHasBeenMade = action.payload
    },

    updateBrochures: (state, action: PayloadAction<Doc[]>) => {
      state.brochures = action.payload
      if(action.payload?.[0]?.created) {
        state.validTill = Date.parse(action.payload[0].created) + ONE_YEAR
      }
      state.allDownloadable =
        state.brochures.filter(item => item.state === READY).length === state.brochures.length &&
        state.drawings.filter(item => item.state === COMPLETE || item.state === AVAILABLE).length === state.drawings.length
    },

    updateDrawings: (state, action: PayloadAction<Doc[]>) => {
      state.drawings = action.payload
      if(action.payload?.[0]?.created) {
        state.validTill = Date.parse(action.payload[0].created) + ONE_YEAR
      }
      state.allDownloadable =
        state.brochures.filter(item => item.state === READY).length === state.brochures.length &&
        state.drawings.filter(item => item.state === COMPLETE || item.state === AVAILABLE).length === state.drawings.length
    },

    updateDrawingState: (state, action: PayloadAction<KeyValuePair>) => {
      const drawing = action.payload.drawing
      const drawingIndex = action.payload.index
      state.drawings[drawingIndex] = drawing
    },

    updateUrlParams: (state, action: PayloadAction<UrlParams>) => {
      state.configId = action.payload.configId
      state.designId = action.payload.designId
      state.productCode = action.payload.productCode
      state.product = action.payload.product
      state.release = action.payload.release
      state.language = action.payload.language
      state.localeFullCode = action.payload.localeFullCode
      state.productType = action.payload.productType
      state.cdProductType = action.payload.cdProductType

      const alpha3 = countryISOMapping[(action.payload.language).split('_')[1]]
      if(alpha3) {
        state.country = countryList.find(item => item.alpha3 === alpha3)?.name
      }

    },

    setContactState: (state, action: PayloadAction<ContactState>) => {
      state.contactState = action.payload
    },

    setNotifyState: (state, action: PayloadAction<ContactState>) => {
      state.notifyState = action.payload
    },

    setCurrentEmail: (state, action: PayloadAction<string>) => {
      state.currentEmail = action.payload
      if (action.payload) {
        window.sessionStorage.setItem("currentEmail",action.payload)
      } else {
        window.sessionStorage.removeItem("currentEmail")
      }
    },

    setSecondUserEmail: (state, action: PayloadAction<string>) => {
      state.secondUserEmail = action.payload
    },

    setSecondUserCount: (state, action: PayloadAction<number>) => {
      state.secondUserCount = action.payload
    },

    setTranslation: (state, action: PayloadAction<TranslationData>) => {
      const terms:KeyValuePair = {}
      for (const term of action.payload.translation) {
        terms[term.key] = term.value
      }
      state.terms = terms
    },

    setCDProducts: (state, action: PayloadAction<any>) => {
      state.cdProducts = action.payload
    },

    setFrontlineCountries: (state, action: PayloadAction<any>) => {
      state.frontlineCountries = action.payload
    },

    setLinkData: (state, action: PayloadAction<LinkInfo>) => {
      state.linkInfo = action.payload
    },

    setCDUri: (state, action: PayloadAction<string>) => {
      state.cdUri = action.payload
    },

    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload
    },

    setLanguages: (state, action: PayloadAction<LanguagesData>) => {
      const languages:LanguageData[] = action.payload.all
      state.languages = languages
    },

    setDomainCountry: (state, action: PayloadAction<KeyValuePair>) => {
      state.domainCountry = action.payload
    },

    setShowTranslationsKeysState: (state, action: PayloadAction<boolean>) => {
      state.showTranslationsKeys = action.payload
    },

    setCanShowTranslationsKeys: (state, action: PayloadAction<boolean>) => {
      state.canShowTranslationsKeys = action.payload
    },

  },
});

export const appActions = appSlice.actions;

export const selectInitialized = (state: RootState) => state.app.initialized;
export const selectRowKey = (state: RootState) => state.app.rowKey;
export const selectBrochures = (state: RootState) => state.app.brochures;
export const selectDrawings = (state: RootState) => state.app.drawings;
export const selectAllDownloadable = (state: RootState) => state.app.allDownloadable;
export const selectValidTill = (state: RootState) => state.app.validTill;

export const selectContactState = (state: RootState) => state.app.contactState;
export const selectContactSuccessful = (state: RootState) => state.app.contactState === "ready";
export const selectNotifyState = (state: RootState) => state.app.notifyState;
export const selectNotifySuccessful = (state: RootState) => state.app.notifyState === "ready";

export const selectOrderHasBeenMade = (state: RootState) => state.app.orderHasBeenMade;

export const selectCurrentEmail = (state: RootState) => state.app.currentEmail;
export const selectSecondUserEmail = (state: RootState) => state.app.secondUserEmail;
export const selectSecondUserCount = (state: RootState) => state.app.secondUserCount;

export const selectTerms = (state: RootState) => state.app.terms;
export const selectProduct = (state: RootState) => state.app.product;
export const selectProductCode = (state: RootState) => state.app.productCode;
export const selectRelease = (state: RootState) => state.app.release;
export const selectConfigurationId = (state: RootState) => state.app.configId;
export const selectDesignId = (state: RootState) => state.app.designId;
export const selectProductType = (state: RootState) => state.app.productType;
export const selectCdProductType = (state: RootState) => state.app.cdProductType;
export const selectLanguage = (state: RootState) => state.app.language;
export const selectLocaleFullCode = (state: RootState) => state.app.localeFullCode;
export const selectLanguages = (state: RootState) => state.app.languages;
export const selectCountry = (state: RootState) => state.app.country;

export const selectCDProducts = (state: RootState) => state.app.cdProducts;
export const selectCDUri = (state: RootState) => state.app.cdUri;

export const selectFrontlineCountries = (state: RootState) => state.app.frontlineCountries;
export const selectDomainCountry = (state: RootState) => state.app.domainCountry;

export const selectValidUrlParams = (state: RootState) => state.app.validUrlParams;
export const selectLinkInfo = (state: RootState) => state.app.linkInfo;

export const selectShowTranslationsKeys = (state: RootState) => state.app.showTranslationsKeys;
export const selectCanShowTranslationsKeys = (state: RootState) => state.app.canShowTranslationsKeys;

export const selectRequestReachedTheBackend = (state: RootState) => state.app.requestReachedTheBackend;
export const selectShowContactForm = (state: RootState) => state.app.showContactForm;
export const selectCadGenerationFailed = (state: RootState) => {
  const failedCads:Doc[] = state.app.drawings.filter(item => item.state === FAILED)
  return failedCads.length>0
}
export const selectCountryCode = (state: RootState) => {
  if(state.app.linkInfo) {
    return countryISOMapping[state.app.linkInfo.destinationCountry] 
  }
  return ''
}

export default appSlice.reducer;
